import { gql, useQuery } from '@apollo/client';
import { Badge, Callout, Text, Tooltip } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router';
import { MockupJobs } from '../common';
import Title from '../elements/Title';
import ListJobs from '../jobs/ListJobs';
import ApplicantCard from '../swiper/ApplicantCard';
import { Error } from './Error';

const JOBS_QUERY = gql`
  {
    getLeads: allJobs(filter: { active: true }, sort: { field: _id, order: ASC }, limit: 3) {
      totalCount
      nodes {
        title
        id
        userId
        company
        active
        createdAt
        threshold
        location {
          id
          name
          address
        }
        applications(
          filter: { score: { gte: 0 }, stage: { equals: 2 }, accepted: { equals: null } }
          sort: { field: score, order: DESC }
          limit: 3
        ) {
          totalCount
          nodes {
            id
            score
            stage
            status
            createdAt
            activityLog {
              createdAt
              status
              stage
              actionType
              message
            }
            candidate {
              firstName
              lastName
              email
              id
            }
          }
        }
      }
    }
    getNext: allJobs(filter: { active: true }, sort: { field: _id, order: ASC }, limit: 3) {
      totalCount
      nodes {
        title
        id
        userId
        company
        active
        createdAt
        threshold
        location {
          id
          name
          address
        }
        applications(
          filter: { stage: { equals: 3 }, score: { gt: 0 }, accepted: { equals: true } }
          sort: { field: score, order: DESC }
          limit: 3
        ) {
          totalCount
          nodes {
            id
            score
            stage
            accepted
            status
            createdAt
            activityLog {
              createdAt
              status
              stage
              actionType
              message
            }
            candidate {
              firstName
              lastName
              email
              id
            }
          }
        }
      }
    }
    activeJobs: allJobs(filter: { active: true }, limit: 15, sort: { field: _id, order: DESC }) {
      totalCount
      nodes {
        title
        id
        userId
        company
        active
        createdAt
        threshold
        location {
          id
          name
          city
          address
        }
        externalRef {
          externalId
          source
        }
        new: applications(filter: { stage: { equals: null } }) {
          totalCount
        }
        leads: applications(filter: { stage: { equals: 2 } }) {
          totalCount
        }
        accepted: applications(filter: { accepted: { equals: true } }) {
          totalCount
        }
        hired: applications(filter: { stage: { equals: 4 } }) {
          totalCount
        }
        notHired: applications(filter: { stage: { equals: 5 } }) {
          totalCount
        }
        manualRejected: applications(filter: { accepted: { equals: false } }) {
          totalCount
        }
        total: applications {
          totalCount
        }
      }
    }
  }
`;
const Home = () => {
  const { t } = useTranslation();
  const { data, error, loading } = useQuery(JOBS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  return (
    <>
      <Title title={t('home.pageTitle')} />
      {Error(error, () => (
        <Callout.Root color="red">
          <Callout.Text className="mb-0">{t('home.errorCallout.message')}</Callout.Text>
        </Callout.Root>
      ))}
      <div className="pt-4 flex">
        <div className="kanban w-1/4">
          <h1>{t('home.leads.title')}</h1>
          <div className="kanban-column px-2">
            {loading && <MockupJobs title={true} />}
            {!loading && data && <ListApplicationInJobs jobs={data.getLeads} />}
          </div>
        </div>
        <div className="kanban w-1/4">
          <h1>{t('home.nextStep.title')}</h1>
          <div className="kanban-column px-2">
            {!data && <MockupJobs title={true} />}
            {data && <ListApplicationInJobs jobs={data.getNext} />}
          </div>
        </div>
        <div className="kanban w-1/2">
          <h1>{t('home.latestJobs.title')}</h1>
          {data?.activeJobs?.nodes?.length === undefined}
          <ListJobs
            loading={loading && data?.activeJobs?.nodes?.length === undefined}
            jobs={data ? data.activeJobs.nodes : null}
            size="small"
          />
        </div>
      </div>
    </>
  );
};
export default Home;

const ListApplicationInJobs = ({
  jobs,
}: {
  jobs: {
    nodes: {
      id: string;
      title: string;
      threshold: number;
      location: { name: string };
      applications: {
        totalCount: number;
        nodes: {
          id: string;
          stage: number;
          score: number;
          status: string;
          accepted: boolean;
          createdAt: string;
          candidate: {
            id: string;
            firstName: string;
            lastName: string;
          };
          aiDetection: {
            isAi: boolean;
          };
          activityLog: {
            createdAt: string;
            actionType: string;
            actionSubType: string;
            message: string;
          }[];
        }[];
      };
    }[];
  };
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = jobs.nodes.map((job) => {
    if (job.applications.nodes.length > 0) {
      return (
        <div key={`job-list-${job.id}`} className="">
          <NavLink to={'/job/' + job.id}>
            <Tooltip
              content={
                <Text size="3">
                  {job.title} {job && job.location && job.location.name && <span> - {job.location.name}</span>}
                </Text>
              }
            >
              <h2 className="truncate">
                {job.title}
                {job && job.location && job.location.name && (
                  <Badge className="mx-2" radius="large">
                    {job.location.name}
                  </Badge>
                )}
              </h2>
            </Tooltip>
          </NavLink>
          {job.applications.nodes.map((application, i) => {
            return (
              <ApplicantCard
                key={`list-application-in-jobs-${i}-${application.id}`}
                jobId={job.id}
                application={application}
                selectCallback={(e) => navigate(`/job/${e.jobId}/${e.applicationId}`)}
                threshold={job.threshold}
              />
            );
          })}
          {job.applications.totalCount > 3 && (
            <div className="showing-top-text">
              {t('home.latestJobs.showingTopOf', {
                shown: job.applications.nodes.length,
                total: job.applications.totalCount,
              })}
            </div>
          )}
        </div>
      );
    } else return null;
  });

  if (list.length === 0)
    return (
      <div className="">
        <h2 className="truncate">{t('home.latestJobs.empty')}</h2>
      </div>
    );
  return list;
};
